import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const FORM_NAME = "forte-inquiry";
const FORM_ENDPOINT = "/__forms.html";

export const ContactSection = ({ presetInterest }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    botField: "",
  });
  const requestedInterest =
    typeof presetInterest === "string"
      ? presetInterest
      : presetInterest?.value || "";
  const requestedSequence =
    typeof presetInterest === "string" ? 0 : presetInterest?.sequence || 0;
  const [interestOverride, setInterestOverride] = useState({
    value: null,
    sequence: -1,
  });
  const interest = interestOverride.sequence === requestedSequence
    ? interestOverride.value || requestedInterest
    : requestedInterest;
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const inquirySubject = `Forte inquiry: ${interest || "General"} - ${
    form.name.trim() || "Website visitor"
  }`;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email, and a short message.");
      return;
    }
    // Honeypot: if filled, silently "succeed" (bot submission).
    if (form.botField) {
      setDone(true);
      return;
    }
    setSubmitting(true);
    const payload = {
      "form-name": FORM_NAME,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      interest,
      message: form.message.trim(),
      subject: inquirySubject,
      "bot-field": form.botField,
    };

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "text/html",
        },
        body: new URLSearchParams(payload).toString(),
      });
      if (!res.ok) {
        throw new Error("Submission failed.");
      }

      setDone(true);
      toast.success("Thank you \u2014 your note has been sent.");
    } catch (err) {
      const detail =
        err?.message ||
        "Something went wrong. Please try again.";
      setError(typeof detail === "string" ? detail : "Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    backgroundColor: "var(--surface-2)",
    border: "1px solid var(--border-c)",
    color: "var(--text)",
    height: 48,
    borderRadius: "var(--radius-sm)",
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section"
      style={{ backgroundColor: "var(--bg-2)" }}
    >
      <div className="container-fx">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <div>
              <p className="eyebrow">{CONTACT.eyebrow}</p>
              <h2
                className="display mt-5"
                style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)", fontWeight: 500 }}
              >
                {CONTACT.headline_lead}{" "}
                <span className="accent-italic">{CONTACT.headline_accent}</span>
              </h2>
              <p
                className="mt-6 max-w-md"
                style={{
                  color: "var(--text-2)",
                  fontSize: "1rem",
                  lineHeight: 1.7,
                }}
              >
                {CONTACT.body}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              className="p-7 sm:p-9"
              style={{
                backgroundColor: "var(--surface)",
                border: "1px solid var(--border-c)",
                borderRadius: "var(--radius-lg)",
              }}
            >
              {done ? (
                <div
                  data-testid="contact-form-success-message"
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <CheckCircle2 size={42} style={{ color: "var(--success)" }} />
                  <h3
                    className="font-serif mt-5"
                    style={{ fontSize: "1.6rem", color: "var(--text)" }}
                  >
                    Note received.
                  </h3>
                  <p
                    className="mt-3 max-w-xs"
                    style={{ color: "var(--text-2)", fontSize: "0.92rem" }}
                  >
                    Thank you for reaching out to Forte by Ryann. We&apos;ll be in
                    touch soon.
                  </p>
                </div>
              ) : (
                <form
                  name={FORM_NAME}
                  method="POST"
                  action={FORM_ENDPOINT}
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  onSubmit={submit}
                  className="space-y-5"
                >
                  <input type="hidden" name="form-name" value={FORM_NAME} />
                  <input type="hidden" name="interest" value={interest} />
                  <input type="hidden" name="subject" value={inquirySubject} />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <Label
                        htmlFor="name"
                        style={{ color: "var(--muted)", fontSize: "0.78rem" }}
                      >
                        Name
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-form-name-input"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Your name"
                        className="mt-2"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="email"
                        style={{ color: "var(--muted)", fontSize: "0.78rem" }}
                      >
                        Email
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-form-email-input"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@email.com"
                        className="mt-2"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="phone"
                      style={{ color: "var(--muted)", fontSize: "0.78rem" }}
                    >
                      Phone <span style={{ opacity: 0.6 }}>(optional)</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="contact-form-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(555) 555-5555"
                      className="mt-2"
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <Label style={{ color: "var(--muted)", fontSize: "0.78rem" }}>
                      I&apos;m interested in
                    </Label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {CONTACT.interests.map((opt) => {
                        const active = interest === opt;
                        return (
                          <button
                            type="button"
                            key={opt}
                            data-testid={`contact-interest-${opt
                              .toLowerCase()
                              .replace(/[^a-z]+/g, "-")}`}
                            onClick={() =>
                              setInterestOverride({
                                value: opt,
                                sequence: requestedSequence,
                              })
                            }
                            aria-pressed={active}
                            style={{
                              fontFamily: "var(--font-sans)",
                              fontSize: "0.74rem",
                              letterSpacing: "0.04em",
                              padding: "0.5rem 0.9rem",
                              borderRadius: "var(--radius-pill)",
                              border: `1px solid ${
                                active ? "var(--bronze)" : "var(--border-c)"
                              }`,
                              backgroundColor: active
                                ? "var(--bronze-soft)"
                                : "transparent",
                              color: active ? "var(--text)" : "var(--muted)",
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                            }}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="message"
                      style={{ color: "var(--muted)", fontSize: "0.78rem" }}
                    >
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-form-message-textarea"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us a little about what you're looking for..."
                      rows={4}
                      className="mt-2"
                      style={{
                        backgroundColor: "var(--surface-2)",
                        border: "1px solid var(--border-c)",
                        color: "var(--text)",
                        borderRadius: "var(--radius-sm)",
                      }}
                    />
                  </div>

                  {/* Honeypot field (hidden from humans; traps bots) */}
                  <input
                    type="text"
                    name="bot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.botField}
                    onChange={update("botField")}
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      width: 1,
                      height: 1,
                      opacity: 0,
                    }}
                    aria-hidden="true"
                  />

                  {error ? (
                    <p
                      role="alert"
                      aria-live="assertive"
                      data-testid="contact-form-error-message"
                      style={{ color: "var(--danger)", fontSize: "0.82rem" }}
                    >
                      {error}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={submitting}
                    className="btn-fx btn-primary w-full"
                    style={{ opacity: submitting ? 0.7 : 1 }}
                  >
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
